import { useRef, useState } from "react";
import { Info } from "lucide-react";
import { createPortal } from "react-dom";
import { SDL_SCORE_LABELS } from "../constants/sdlMaturity";
import { SDLScoringGuideButton } from "./SDLScoringGuideModal";

interface PreviousAnswer {
  score: number | null;
  details: string;
}

interface SDLAssessmentQuestionProps {
  index: number;
  question: {
    id: string;
    name: string;
    description: string;
    practice: string;
    criteria?: string;
  };
  value: number | null;
  details: string;
  onChange: (
    questionId: string,
    field: "score" | "details",
    value: any,
  ) => void;
  disabled?: boolean;
  previousAnswer?: PreviousAnswer | null;
  previousPeriod?: string | null;
}

const scoreLabels: Record<number, string> = SDL_SCORE_LABELS;

export default function SDLAssessmentQuestion({
  index,
  question,
  value,
  details,
  onChange,
  disabled = false,
  previousAnswer = null,
  previousPeriod = null,
}: SDLAssessmentQuestionProps) {
  const hasPreviousAnswer = previousAnswer && previousAnswer.score !== null;
  const [showPrevious, setShowPrevious] = useState(false);
  const iconRef = useRef<HTMLSpanElement>(null);

  const getPopoverStyle = (): React.CSSProperties => {
    if (!iconRef.current) return { display: "none" };
    const rect = iconRef.current.getBoundingClientRect();
    return {
      position: "fixed",
      left: rect.left + rect.width / 2,
      top: rect.bottom + 8,
      transform: "translateX(-50%)",
      zIndex: 9999,
    };
  };

  return (
    <div className="w-full">
      <div className="mb-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {index + 1}. {question.name}
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
              {question.description}
            </p>
          </div>
        </div>
        <span className="inline-block mt-1.5 text-xs font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded">
          {question.practice}
        </span>

        {/* Assessment Criteria */}
        {question.criteria && (
          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800/30 rounded-md">
            <h5 className="text-xs font-semibold text-blue-800 dark:text-blue-300 uppercase tracking-wide mb-1.5">
              Assessment Criteria
            </h5>
            <ul className="space-y-1">
              {question.criteria
                .split("\n")
                .filter(Boolean)
                .map((line, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 leading-relaxed min-w-0"
                  >
                    <span className="mt-1.5 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500" />
                    {line.trim()}
                  </li>
                ))}
            </ul>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Maturity Score
            </label>
            <SDLScoringGuideButton />
            {hasPreviousAnswer && (
              <span
                ref={iconRef}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 border border-blue-200 dark:border-blue-800 transition-colors cursor-pointer"
                onMouseEnter={() => setShowPrevious(true)}
                onMouseLeave={() => setShowPrevious(false)}
              >
                <Info className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Previous Answer</span>
                {showPrevious &&
                  createPortal(
                    <div
                      style={getPopoverStyle()}
                      className="pointer-events-none"
                    >
                      {/* Arrow */}
                      <div className="flex justify-center">
                        <div className="w-0 h-0 border-l-[7px] border-l-transparent border-r-[7px] border-r-transparent border-b-[7px] border-b-white dark:border-b-gray-900" />
                      </div>
                      <div className="w-72 rounded-xl bg-white dark:bg-gray-900 shadow-[0_20px_60px_-12px_rgba(0,0,0,0.25)] dark:shadow-[0_20px_60px_-12px_rgba(0,0,0,0.6)] ring-1 ring-black/[0.06] dark:ring-white/[0.08] overflow-hidden">
                        {/* Header */}
                        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                              Previous Assessment
                            </span>
                            {previousPeriod && (
                              <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded-md">
                                {previousPeriod}
                              </span>
                            )}
                          </div>
                        </div>
                        {/* Body */}
                        <div className="p-4">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-11 h-11 rounded-xl flex items-center justify-center shadow-md ${previousAnswer!.score === -1 ? "bg-gray-500 dark:bg-gray-600 shadow-gray-500/25" : "bg-blue-600 dark:bg-blue-500 shadow-blue-500/25"}`}
                            >
                              <span
                                className="text-xl font-extrabold text-white"
                                style={{ fontFeatureSettings: '"tnum"' }}
                              >
                                {previousAnswer!.score === -1
                                  ? "N/A"
                                  : previousAnswer!.score}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                {scoreLabels[previousAnswer!.score!]}
                              </div>
                              <div className="text-[11px] text-gray-400 dark:text-gray-500 font-medium">
                                {previousAnswer!.score === -1
                                  ? "Marked N/A in previous assessment"
                                  : "Maturity Score from previous assessment"}
                              </div>
                            </div>
                          </div>
                          {previousAnswer!.details && (
                            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                              <div className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                                Implementation Details
                              </div>
                              <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-3">
                                {previousAnswer!.details}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>,
                    document.body,
                  )}
              </span>
            )}
          </div>
          <div className="grid grid-cols-6 gap-2">
            {[0, 1, 2, 3, 4].map((score) => (
              <button
                key={score}
                type="button"
                disabled={disabled}
                onClick={() => onChange(question.id, "score", score)}
                className={`py-2.5 px-2 text-sm font-medium rounded-md border transition-colors truncate ${
                  value === score
                    ? score === 0
                      ? "bg-red-100 border-red-300 text-red-800 dark:bg-red-900/30 dark:border-red-700 dark:text-red-200"
                      : score === 1
                        ? "bg-orange-100 border-orange-300 text-orange-800 dark:bg-orange-900/30 dark:border-orange-700 dark:text-orange-200"
                        : score === 2
                          ? "bg-yellow-100 border-yellow-300 text-yellow-800 dark:bg-yellow-900/30 dark:border-yellow-700 dark:text-yellow-200"
                          : score === 3
                            ? "bg-green-100 border-green-300 text-green-800 dark:bg-green-900/30 dark:border-green-700 dark:text-green-200"
                            : "bg-emerald-100 border-emerald-300 text-emerald-800 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-200"
                    : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {score} - {scoreLabels[score]}
              </button>
            ))}
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChange(question.id, "score", -1)}
              className={`py-2.5 px-2 text-sm font-medium rounded-md border transition-colors truncate ${
                value === -1
                  ? "bg-gray-200 border-gray-400 text-gray-800 dark:bg-gray-600 dark:border-gray-500 dark:text-gray-200"
                  : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              N/A
            </button>
          </div>
          {value === -1 && (
            <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700/40 rounded-md">
              <p className="text-xs text-amber-700 dark:text-amber-400">
                <span className="font-semibold">Justification required:</span>{" "}
                You must provide a written justification below explaining why
                this control does not apply to your product or development
                context, based on your threat model or product security context.
              </p>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {value === -1 ? "N/A Justification" : "Implementation Details"}{" "}
            <span className="text-red-500">*</span>
          </label>
          <textarea
            value={details}
            onChange={(e) => onChange(question.id, "details", e.target.value)}
            onClick={() => {
              if (
                !details &&
                !disabled &&
                hasPreviousAnswer &&
                previousAnswer.details
              ) {
                onChange(question.id, "details", previousAnswer.details);
              }
            }}
            disabled={disabled}
            rows={3}
            className={`block w-full min-h-[80px] px-3 py-2 border rounded-md shadow-sm focus:outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:bg-gray-100 dark:disabled:bg-gray-900 disabled:cursor-not-allowed text-sm ${
              value === -1
                ? "border-amber-300 dark:border-amber-600 focus:ring-amber-500 focus:border-amber-500"
                : "border-gray-300 dark:border-gray-600 focus:ring-green-500 focus:border-green-500"
            }`}
            placeholder={
              value === -1
                ? "Explain why this control does not apply to your product (e.g., based on threat model, product architecture, or security context)..."
                : hasPreviousAnswer && previousAnswer.details
                  ? previousAnswer.details
                  : "Please insert answer to the following assessment criteria questions, as applicable:"
            }
          />
        </div>
      </div>
    </div>
  );
}
